import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import dayjs from 'dayjs';

const Orders = ({ unitCode, companyCode, unitType, userId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get("filterType") || "year";
  const urlUnitCode = searchParams.get('unit');
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const isRestaurant = unitType?.toLowerCase() === 'restaurant';
  const isHotel = unitType?.toLowerCase() === 'hotel';
  const isFoodcourt = unitType?.toLowerCase() === 'foodcourt';
  const itemType = isRestaurant ? 'Table' : isHotel ? 'Room' : 'Customer';
  const idField = isRestaurant ? 'tableId' : isHotel ? 'roomId' : 'customerId';

  const fetchOrders = async () => {
    try {
      const effectiveUnitCode = urlUnitCode || unitCode;
      if (!effectiveUnitCode) {
        setError('No unit code provided. Please specify a unit code in the URL (e.g., ?unit=UNIT123) or ensure you are logged in with a valid unit.');
        setLoading(false);
        return;
      }

      console.log('OrderHistory: Fetching orders for unit:', effectiveUnitCode, 'userId:', userId);
      const ordersQuery = query(
        collection(db, 'orders'),
        where('unitCode', '==', effectiveUnitCode),
        orderBy('createdAt', 'desc')
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      /*const ordersList = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));*/
      let ordersList = ordersSnapshot.docs.map(doc => ({
  id: doc.id,
  ...doc.data(),
}));

// Apply date filters
const now = dayjs();

if (filterType === "today") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return orderDate.isSame(now, "day");
  });
}

else if (filterType === "month") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return (
      orderDate.month() === now.month() &&
      orderDate.year() === now.year()
    );
  });
}

else if (filterType === "year") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return orderDate.year() === now.year();
  });
}
      console.log('OrderHistory: Fetched orders:', { ordersList, unitCode: effectiveUnitCode, userId });
      setOrders(ordersList);
      setLoading(false);
      if (ordersList.length === 0) {
        setError(`No orders found for unit ${effectiveUnitCode}.`);
      }
    } catch (err) {
      console.error('OrderHistory: Error fetching orders:', { error: err, unitCode, userId });
      setError('Failed to load orders.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [urlUnitCode, unitCode, userId]);

  const groupOrdersByDate = (orders) => {
    const grouped = {};
    orders.forEach((order) => {
      if (order.createdAt) {
        const date = order.createdAt.toDate
          ? order.createdAt.toDate().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : 'Unknown Date';
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(order);
      }
    });
    return grouped;
  };

  const groupedOrders = groupOrdersByDate(orders);

  const handleBack = () => {
    console.log('OrderHistory: Navigating to /manager/home', { unitCode, userId });
    navigate(`/unit_manager/home?unit=${unitCode}`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/*<Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Order History 
      </Typography>*/}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Order History (
        {filterType === "today"
          ? "Today"
          : filterType === "month"
          ? "Month"
          : "Year"}
        )
      </Typography>

      {error && (
        <Typography variant="h6" color="error" sx={{ textAlign: 'center', mb: 3 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          Loading orders...
        </Typography>
      ) : (
        <Box>
          {Object.keys(groupedOrders).length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No orders available.
            </Typography>
          ) : (
            Object.keys(groupedOrders).map((date) => (
              <Box key={date} sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {date}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {groupedOrders[date].map((order, index) => (
                    <React.Fragment key={order.id}>
                      <ListItem
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography variant="h6">
                            {itemType} ID: {order[idField]}
                          </Typography>
                          <Typography variant="body1" color={order.status === 'Delivered' ? 'success.main' : 'text.primary'}>
                            Status: {order.status}
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          Order ID: {order.orderId}
                        </Typography>
                        <Typography variant="body1">
                          Time: {order.createdAt.toDate
                            ? order.createdAt.toDate().toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                          Total Amount: ₹{order.grandTotal || order.totalAmount || 0}
                        </Typography>
                        {isFoodcourt && (
                          <>
                            <Typography variant="body1">
                              Customer Name: {order.customerName || 'N/A'}
                            </Typography>
                            <Typography variant="body1">
                              Customer Mobile: {order.customerMobile || 'N/A'}
                            </Typography>
                          </>
                        )}
                        <Divider sx={{ my: 1, width: '100%' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Items:
                        </Typography>
                        {order.items.map((item, itemIndex) => (
                          <Typography key={itemIndex} variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                            {item.name || 'Unknown Item'} (x{item.quantity ?? 1}) - ₹{item.totalPrice || (item.quantity ?? 1) * (item.price ?? 0)} - Status: {item.status || 'Confirmed'}
                          </Typography>
                        ))}
                      </ListItem>
                      {index < groupedOrders[date].length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ))
          )}
        </Box>
      )}

      <Button variant="contained" color="primary" onClick={handleBack} sx={{ mt: 3 }}>
        Back to Home
      </Button>
    </Container>
  );
};

export default Orders;